import { FormFields, sanitizeTiers, useForm } from '@sequencehq/utils'
import {
  greaterThan,
  greaterThanEqualTo,
  required
} from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import {
  BillingType,
  BillingFrequency,
  PricingEditorReducerState,
  PricingModel,
  RecursivePartial,
  SeatBasedProrationStrategy
} from 'common/drawers/PricingEditor/domain'
import { defaultAvailableFrequenciesOptions } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { useMemo, useCallback, useRef } from 'react'
import { useCommonFieldConfig } from '../common/useCommonFieldConfig'
import { useSyncFormValidity } from '../common/useSyncFormValidity'
import deepmerge from 'deepmerge'
import { useNavigate } from 'react-router-dom'
import { Currency } from '@sequencehq/api'
import {
  getAvailableSeatOveragesFrequenciesFromSelectedPriceFrequency,
  getNewOverageFrequencyForBillingFrequency
} from './seatBasedPrice.utils'
import { tiersValidator } from 'common/drawers/PricingEditor/utils/validators/tiers.validators'
import { commonPriceDetails } from '../common/ReadOnlyDetails/readOnlyDetails.utils'
import { getBillingFrequencyMultiplier } from 'common/drawers/PricingEditor/utils/getBillingFrequencyMultiplier'
import { floatSafeMultiply } from '@sequencehq/utils'
import { enforceMinimumPrecision } from '@sequencehq/utils'
import { pick } from 'lodash/fp'
import { dequal } from 'dequal'
import { match } from 'ts-pattern'
import { useSeatMetrics } from 'Seats/SeatMetric/useSeatMetrics'

export type CommonFields = Omit<
  PricingEditorReducerState['data']['pricingEditorData']['common'],
  'pricingModel'
>
export type SeatBasedPriceFields =
  PricingEditorReducerState['data']['pricingEditorData']['SEAT_BASED_GRADUATED']

type SeatBasedPriceFormFieldConfig = Omit<
  FormFields<CommonFields>,
  'pricingModel'
> &
  Omit<
    FormFields<SeatBasedPriceFields>,
    'seatTypeId' | 'overagesBillingFrequency' | 'prorationStrategy'
  > & {
    currency: FormFields<CommonFields>['currency'] & {
      hidden: boolean
    }
    seatMetricId: FormFields<SeatBasedPriceFields>['seatMetricId']
    overagesBillingFrequency: Omit<
      FormFields<SeatBasedPriceFields>['overagesBillingFrequency'],
      'options'
    > & {
      options: { value: string; label: string; description: string }[]
      selected: { value: string; label: string; description: string }
    }
    prorationStrategy: Omit<
      FormFields<SeatBasedPriceFields>['prorationStrategy'],
      'options'
    > & {
      options: { value: string; label: string; description: string }[]
      selected: { value: string; label: string; description: string }
    }
    pricePerSeat: FormFields<SeatBasedPriceFields>['pricePerSeat'] & {
      hidden: boolean
    }
    prorateFlatFees: FormFields<SeatBasedPriceFields>['prorateFlatFees'] & {
      hidden: boolean
    }
  }

type UseSeatBasedPriceForm = () => {
  fieldsConfig: SeatBasedPriceFormFieldConfig
  conditions: {
    isInAdvanceSelected: boolean
    isContractedMinimumSet: boolean
    isBillingFrequencyLessThanMonthly: boolean
    isProrateStrategyIncrements: boolean
  }
  currency: Currency
  priceDetails: { label: string; value: string }[]
}

const availableBillingTypeOptions: { value: BillingType; label: string }[] = [
  { label: 'In arrears', value: 'IN_ARREARS' },
  { label: 'In advance', value: 'IN_ADVANCE' }
]

const adjustLinearSeatBasedPrice = (params: {
  ratio: number
  pricePerSeat: string
}) => {
  return {
    pricePerSeat: enforceMinimumPrecision(2)(
      floatSafeMultiply(+params.pricePerSeat, params.ratio, 2)
    )
  }
}

const adjustGraduatedSeatBasedPrice = (params: {
  ratio: number
  tiers: SeatBasedPriceFields['tiers']
}) => {
  return {
    tiers: params.tiers.map(tier => {
      const seatPricePrecision = tier.unitPrice.split('.')[1]?.length ?? 0
      const flatFeePrecision = tier.flatFee.split('.')[1]?.length ?? 0

      const seatTargetPrecision = Math.max(seatPricePrecision, 2)
      const feeTargetPrecision = Math.max(flatFeePrecision, 2)

      return {
        ...tier,
        unitPrice: enforceMinimumPrecision(seatTargetPrecision)(
          floatSafeMultiply(+tier.unitPrice, params.ratio, seatTargetPrecision)
        ),
        flatFee: enforceMinimumPrecision(feeTargetPrecision)(
          floatSafeMultiply(+tier.flatFee, params.ratio, feeTargetPrecision)
        )
      }
    })
  }
}

export const sanitizeFormData = (
  pricingModel: PricingModel,
  formData: SeatBasedPriceFields & CommonFields
): RecursivePartial<PricingEditorReducerState['data']['pricingEditorData']> => {
  const { name, currency, ...rest } = formData

  return {
    common: {
      name,
      currency: formData.currency
    },
    [pricingModel]: {
      ...rest
    }
  }
}

export const prorationStrategyOptions: {
  value: SeatBasedProrationStrategy
  label: string
  description: string
}[] = [
  {
    value: 'USE_MAXIMUM',
    label: 'Charge full amount (no proration)',
    description:
      "'Charge the full monthly price for any seats added in the current billing period.'"
  },
  {
    value: 'PRORATE_INCREMENTS',
    label: 'Prorate added seats only (recommended)',
    description:
      'Charge a prorated fee for seats added in the billing period. Removed seats are charged the full fee.'
  },
  {
    value: 'PRORATE_ALL_CHANGES',
    label: 'Prorate added and removed seats',
    description:
      'Charge a prorated fee for any added and removed seats in the billing period.'
  }
]

export const useSeatBasedPriceForm: UseSeatBasedPriceForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const navigate = useNavigate()
  const tiersManuallyEditedRef = useRef(false)
  const lastAutoPopulatedFirstTierRef = useRef<string | null>(null)
  // Metric options aren't in the form config; resolve the Details label here.
  const { metrics: seatMetrics } = useSeatMetrics()

  const pricingModel = useMemo(
    () =>
      pricingEditorContext.queries.rawData.data.pricingEditorData.common
        .pricingModel,
    [
      pricingEditorContext.queries.rawData.data.pricingEditorData.common
        .pricingModel
    ]
  )

  const commonFields = useCommonFieldConfig<
    SeatBasedPriceFields & CommonFields
  >()

  const {
    fields,
    queries: formQueries,
    mutators: formMutators
  } = useForm({
    value: {
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.common,
      ...(pricingEditorContext.queries.rawData.data.pricingEditorData[
        pricingModel
      ] as SeatBasedPriceFields & CommonFields)
    },
    reinitializeOnValueChange: (params: {
      newData: SeatBasedPriceFields & CommonFields
      prevData: (SeatBasedPriceFields & CommonFields) | undefined
    }) => {
      const propertiesToCompare = pick([
        'productId',
        'listPriceId',
        'pricingModel'
      ])
      return !dequal(
        propertiesToCompare(params.newData),
        propertiesToCompare(params.prevData)
      )
    },
    fieldConfiguration: [
      ...commonFields.fields,
      {
        property: 'billingFrequency',
        validation: [required],
        options: defaultAvailableFrequenciesOptions
      },
      {
        property: 'billingType',
        validation: [required],
        options: availableBillingTypeOptions
      },
      {
        property: 'pricePerSeat',
        validation:
          pricingModel === 'SEAT_BASED_LINEAR'
            ? [required, greaterThan(0, "Price per seat can't be zero")]
            : []
      },
      {
        property: 'tiers',
        validation:
          pricingModel === 'SEAT_BASED_GRADUATED'
            ? [required, tiersValidator]
            : []
      },
      {
        property: 'contractedMinimumSeats',
        validation: [
          required,
          greaterThanEqualTo(0, 'Please enter at least 0 seats')
        ]
      },
      {
        property: 'prorationStrategy',
        validation: [required]
      },
      {
        property: 'overagesBillingFrequency',
        validation: [required]
      },
      {
        property: 'seatMetricId',
        validation: [required]
      },
      {
        property: 'prorateFlatFees'
      }
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    onChange: newData => {
      pricingEditorContext.mutators.common.updatePricingEditorData(
        sanitizeFormData(pricingModel, newData)
      )
    }
  })

  useSyncFormValidity({
    isFormValid: formQueries.isValid,
    formValidationResults: formQueries.validationResults,
    additionalFields: [
      'billingFrequency',
      'billingType',
      'overagesBillingFrequency',
      'prorateFlatFees',
      'prorationStrategy',
      'seatMetricId'
    ]
  })

  /**
   * Handles auto-population of first tier upper bound when user enters included seats.
   * Only applies to graduated pricing model and preserves manual tier configuration.
   */
  const handleContractedMinimumSeatsChange = useCallback(
    (newMinimumSeats: string) => {
      // Non-graduated pricing: just update the field
      if (pricingModel !== 'SEAT_BASED_GRADUATED') {
        return formMutators.updateFormData({
          contractedMinimumSeats: newMinimumSeats
        })
      }

      const currentTiers = formQueries.formData.tiers ?? []
      if (currentTiers.length === 0) {
        return formMutators.updateFormData({
          contractedMinimumSeats: newMinimumSeats
        })
      }

      const firstTier = currentTiers[0]
      const minimumSeatsValue = parseInt(newMinimumSeats || '0')
      // We intentionally do not consider currentMinimumSeatsValue here
      // to avoid overwriting when users type values that transiently match

      // Check if we should auto-populate the first tier
      const isValidNumber = minimumSeatsValue > 0
      const isDefaultOrEmpty =
        !firstTier?.lastUnit ||
        firstTier.lastUnit === '' ||
        firstTier.lastUnit === '0' ||
        firstTier.lastUnit === '1'
      // Only consider it previously auto-populated if we set it ourselves
      const wasPreviouslyAutoPopulatedByUs =
        !!lastAutoPopulatedFirstTierRef.current &&
        firstTier?.lastUnit === lastAutoPopulatedFirstTierRef.current

      const shouldAutoPopulate =
        isValidNumber &&
        !tiersManuallyEditedRef.current &&
        (isDefaultOrEmpty || wasPreviouslyAutoPopulatedByUs)

      if (shouldAutoPopulate) {
        const updatedTiers = currentTiers.map((tier, index) =>
          index === 0
            ? { ...tier, lastUnit: minimumSeatsValue.toString() }
            : tier
        )

        formMutators.updateFormData({
          contractedMinimumSeats: newMinimumSeats,
          tiers: sanitizeTiers(updatedTiers)
        })
        // Track that we auto-populated the first tier
        lastAutoPopulatedFirstTierRef.current = minimumSeatsValue.toString()
      } else {
        formMutators.updateFormData({
          contractedMinimumSeats: newMinimumSeats
        })
      }
    },
    [pricingModel, formQueries.formData.tiers, formMutators]
  )

  const overagesFrequencies = useMemo(() => {
    return getAvailableSeatOveragesFrequenciesFromSelectedPriceFrequency(
      formQueries.formData.billingFrequency
    )
  }, [formQueries.formData.billingFrequency])

  const enhancedFields = useMemo(() => {
    return deepmerge(
      fields,
      {
        tiers: {
          onChange: (newTiers: SeatBasedPriceFields['tiers']) => {
            // Mark tiers as manually edited so auto-population does not override user input
            tiersManuallyEditedRef.current = true
            fields.tiers.onChange(newTiers)
          }
        },
        currency: {
          ...fields.currency,
          options: fields.currency.options.map(option => ({
            ...option,
            label: option.label.split(' ')[0]
          })),
          hidden: Boolean(
            pricingEditorContext.queries.rawData.configuration.currency
          )
        },
        prorationStrategy: {
          options: prorationStrategyOptions,
          selected: prorationStrategyOptions.find(
            opt => formQueries.formData.prorationStrategy === opt.value
          )
        },
        overagesBillingFrequency: {
          options: overagesFrequencies,
          selected: overagesFrequencies.find(
            opt => formQueries.formData.overagesBillingFrequency === opt.value
          )
        },
        pricePerSeat: {
          hidden: pricingModel === 'SEAT_BASED_GRADUATED'
        },
        prorateFlatFees: {
          hidden:
            pricingModel !== 'SEAT_BASED_GRADUATED' ||
            (formQueries.formData.tiers ?? [])
              .map(tier => tier.flatFee)
              .filter(flatFee => +flatFee > 0).length === 0
        },
        contractedMinimumSeats: {
          onChange: handleContractedMinimumSeatsChange
        },
        billingType: {
          onChange: (newBillingType: BillingType) => {
            const needsOverageFrequency =
              newBillingType === 'IN_ADVANCE' &&
              formQueries.formData.overagesBillingFrequency === 'NEVER'

            return formMutators.updateFormData({
              billingType: newBillingType,
              ...(needsOverageFrequency
                ? {
                    // Seat-based forms never offer PER_EVENT, so the runtime
                    // frequency is always a valid SeatOveragesBillingFrequency.
                    overagesBillingFrequency: formQueries.formData
                      .billingFrequency as Exclude<
                      BillingFrequency,
                      'PER_EVENT'
                    >
                  }
                : {})
            })
          }
        },
        billingFrequency: {
          onChange: (newFrequency: BillingFrequency) => {
            const ratio = getBillingFrequencyMultiplier({
              fromFrequency: formQueries.formData.billingFrequency,
              toFrequency: newFrequency
            })

            const newOverageFrequency =
              getNewOverageFrequencyForBillingFrequency({
                newBillingFrequency: newFrequency,
                currentOveragesFrequency:
                  formQueries.formData.overagesBillingFrequency
              })

            if (pricingModel === 'SEAT_BASED_LINEAR') {
              return formMutators.updateFormData({
                billingFrequency: newFrequency,
                overagesBillingFrequency: newOverageFrequency,
                ...adjustLinearSeatBasedPrice({
                  ratio,
                  pricePerSeat: formQueries.formData.pricePerSeat
                })
              })
            }

            return formMutators.updateFormData({
              billingFrequency: newFrequency,
              overagesBillingFrequency: newOverageFrequency,
              ...adjustGraduatedSeatBasedPrice({
                ratio,
                tiers: formQueries.formData.tiers ?? []
              })
            })
          }
        }
      },
      {
        arrayMerge: (_, source: unknown[]) => source
      }
    ) as SeatBasedPriceFormFieldConfig
  }, [
    fields,
    navigate,
    pricingModel,
    overagesFrequencies,
    formQueries.formData.overagesBillingFrequency,
    formQueries.formData.prorationStrategy,
    pricingEditorContext.queries.rawData.configuration.currency,
    handleContractedMinimumSeatsChange,
    formQueries.formData.billingFrequency,
    formMutators.updateFormData,
    formQueries.formData.pricePerSeat,
    formQueries.formData.tiers
  ])

  const isInAdvanceSelected = useMemo(
    () => formQueries.formData.billingType == 'IN_ADVANCE',
    [formQueries.formData.billingType]
  )
  const isContractedMinimumSet = useMemo(
    () => parseInt(formQueries.formData.contractedMinimumSeats) > 0,
    [formQueries.formData.contractedMinimumSeats]
  )
  const isBillingFrequencyLessThanMonthly = useMemo(
    () =>
      isInAdvanceSelected &&
      ['QUARTERLY', 'HALF_YEARLY', 'YEARLY'].includes(
        formQueries.formData.billingFrequency
      ),
    [isInAdvanceSelected, formQueries.formData.billingFrequency]
  )
  const isProrateStrategyIncrements = useMemo(
    () => formQueries.formData.prorationStrategy === 'PRORATE_INCREMENTS',
    [formQueries.formData.prorationStrategy]
  )

  const prorationDetail = useMemo(
    () => ({
      label: 'Prorate overage fees',
      value: match(formQueries.formData.prorationStrategy)
        .with('PRORATE_INCREMENTS', 'PRORATE_ALL_CHANGES', () => 'Yes')
        .with('USE_MAXIMUM', () => 'No')
        .exhaustive()
    }),
    [formQueries.formData.prorationStrategy]
  )

  const priceDetails = useMemo(
    () => [
      ...commonPriceDetails({
        taxCategoryName:
          pricingEditorContext.queries.rawData.data.product.taxCategoryName,
        revenueRecognitionMethod:
          pricingEditorContext.queries.rawData.data.product
            .revenueRecognitionMethod,
        pricingModel,
        billingFrequency: formQueries.formData.billingFrequency,
        billingType: formQueries.formData.billingType
      }),
      {
        label: 'Seat metric',
        value:
          seatMetrics.find(
            metric => metric.id === formQueries.formData.seatMetricId
          )?.label ?? 'Not selected'
      },
      prorationDetail
    ],
    [
      seatMetrics,
      formQueries.formData.billingFrequency,
      formQueries.formData.billingType,
      formQueries.formData.seatMetricId,
      prorationDetail,
      pricingModel,
      pricingEditorContext.queries.rawData.data.product.taxCategoryName,
      pricingEditorContext.queries.rawData.data.product.revenueRecognitionMethod
    ]
  )

  return {
    fieldsConfig: enhancedFields,
    currency: formQueries.formData.currency,
    conditions: {
      isInAdvanceSelected,
      isContractedMinimumSet,
      isBillingFrequencyLessThanMonthly,
      isProrateStrategyIncrements
    },
    priceDetails
  }
}
